<template>
  <div class="p-0">
    <div v-if="!dataAvailable" class="text-center">
      <h5 class="m-0 p-3">Loading data.. <font-awesome-icon :icon="['fas', 'spinner']" class="ml-2 fa-spin" size="xs" /></h5>
    </div>
    <b-container v-show="dataAvailable" fluid class="p-0 h-100">
      <slot><!-- Space for Headers, using parameter boxes two rows do fit --></slot>
      <b-row no-gutters class="pt-3">
        <b-col cols="12">
          <highcharts id="castChart" ref="castChart" :options="castChartOptions" :highcharts="highcharts"
          :style="{height: 'calc(100vh - ' + chartHeight}">
          </highcharts>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts/highstock'
import * as moment from 'moment/moment'
import Helpers from '@/mixins/helpers'

require('highcharts/modules/exporting')(Highcharts)
require('highcharts/modules/no-data-to-display')(Highcharts)

export default {
  name: 'CastChart',
  props: {
    depthKey: {
      type: String,
      required: true
    },
    parameters: { // Array of String with parameter identifiers
      type: Array,
      required: true
    },
    chartHeight: {
      type: String,
      required: true
    },
    downloadLinkCsv: {
      type: String,
      required: false
    }
  },
  mixins: [
    Helpers
  ],
  components: {
    highcharts: Chart
  },
  data () {
    return {
      dataAvailable: false,
      dataset: null,
      depthData: [],
      highcharts: Highcharts,
      castChartOptions: {
        deep: true,
        chart: {
          type: 'spline',
          inverted: true,
          reflow: true,
          spacing: [0, 5, 5, 5]
        },
        title: {
          text: 'Water column data',
          align: 'left'
        },
        xAxis: {
          reversed: true,
          title: {
            enabled: true,
            text: 'Depth',
            style: {
              fontWeight: 'bold'
            }
          }
        },
        yAxis: [],
        series: [],
        tooltip: {
          headerFormat: '<span style="font-size: 10px"> {point.key:.2f} </span><br/>'
        },
        exporting: {
          sourceWidth: 300,
          sourceHeight: 600
        },
        colors: [

          '#4F94CF',
          '#55BCEB',
          '#E66F4D',
          '#9FB820',
          '#F47FA0',
          '#666666',

          '#ff9d3a',
          '#003a7d',
          '#c701ff',
          '#4ecb8d',
          '#d83034',
          '#9e858e',

          '#082a54',
          '#f0c571',
          '#e02b35',
          '#59a89c',
          '#a559aa',
          '#cecece',

          '#17a2b8', // info (cyan)
          '#ffc107', // warning (yellow)
          '#dc3545', // danger (red)
          '#28a745', // success (green)
          '#007bff', // primary (blue)
          '#6c757d', // secondary
          '#343a40' // dark
        ],
        credits: {
          enabled: false
        },
        lang: {
          noData: 'No data available'
        },
        noData: {
          style: {
            fontWeight: 'bold',
            fontSize: '15px',
            color: '#343a40'
          }
        },
        legend: {
          enabled: false
        }
      },
      castChart: null
    }
  },
  computed: {
    ...mapState(['selectedAsyncDataset', 'platforms']),
    ...mapGetters(['asyncDatasetSelected'])
  },
  mounted: function () {
    // Subscribe to store mutations
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'add_async_dataset') {
        console.debug('cast widget: delayed rendering of ' + mutation.payload.platform_id + ' / ' + mutation.payload.dataset_id)
        this.loadData(mutation.payload.platform_id, mutation.payload.dataset_id)
      }
    })

    // Set chart reference
    this.castChart = this.$refs.castChart.chart
    if (this.downloadLinkCsv) {
      // TODO more dynamic
      this.castChartOptions.exporting = { buttons: { contextButton: { menuItems: {} } }, menuItemDefinitions: {} }
      this.castChartOptions.exporting.buttons.contextButton.menuItems = ['viewFullscreen', 'separator', 'downloadPNG',
        'downloadSVG', 'downloadPDF', 'downloadJPEG', 'separator', 'downloadCSV']
      this.castChartOptions.exporting.menuItemDefinitions.downloadCSV = {
        onclick: (event) => {
          console.log('cast widget: download data as CSV')
          // call download link
          window.open('https://beluga.geomar.de')
          return true
        },
        text: 'Download raw data as CSV'
      }
    }
    // Load dataset if already downloaded. Otherwise, 'add_async_dataset' mutation will trigger chart rendering
    if (this.asyncDatasetSelected !== null && 'data' in this.asyncDatasetSelected) {
      console.debug('cast widget: direct rendering of ' + this.asyncDatasetSelected.platform_id + ' / ' + this.asyncDatasetSelected.dataset_id)

      // FIXME: Workaround because of highcharts reflow bug
      const widget = this
      window.setTimeout(function () {
        widget.loadData(widget.asyncDatasetSelected.platform_id, widget.asyncDatasetSelected.dataset_id)
      }, 0)
    }
  },
  beforeDestroy () {
    // Unsubscribe from mutation
    this.unsubscribe()
  },
  methods: {
    loadData: function () {
      // Reset chart
      this.clearChart()

      // Render chart
      if (this.asyncDatasetSelected !== null) {
        this.dataset = this.asyncDatasetSelected
        this.updateChartData()
      }
    },
    updateChartData: function () {
      // Init chart
      const initialised = this.initChart()
      if (!initialised) {
        return
      }
      // Load data into chart
      let yAxisCount = 0
      // add data
      this.parameters.forEach(p => {
        const values = this.getParameterByName(p.id)
        if (values !== null) {
          if (!('data_cached' in values)) {
            if ('data' in values && values.data.length > 0) {
              values.data_cached = this.prepareGraph(values.data)
              console.debug('cast widget: caching chart data for parameter ' + p.name)
            } else {
              values.data_cached = null
            }
          }
          if (values.data_cached !== null) {
            this.updateYAxisData(yAxisCount, values.data_cached, values, p.name)
            yAxisCount++
          }
        }
      })
      // Force reflow chart
      this.castChart.reflow()
    },
    initChart: function () {
      const depth = this.getParameterByName(this.depthKey)
      if (depth == null) {
        return false
      }
      this.depthData = depth.data
      // set dataset timestamp
      this.datetime = moment(this.depthData[0].ts).format('MMM D YYYY HH:mm:ss')
      // set x axis parameters
      this.castChartOptions.xAxis.title.text = 'Depth [' + depth.unit.symbol + ']'
      this.castChartOptions.tooltip.headerFormat =
        '<span style="font-size: 10px"> {point.key:.2f} ' + depth.unit.symbol + ' </span><br/>'
      this.dataAvailable = true
      return true
    },
    getParameterByName: function (id) {
      if (id in this.dataset.data.parameters) {
        return this.dataset.data.parameters[id]
      }
      return null
    },
    prepareGraph: function (dataPoints) {
      // FIXME: Performance!
      const graph = []
      dataPoints.forEach(dp => {
        const xval = this.getValuePerTimestamp(dp.ts)
        if (xval !== null) {
          graph.push([xval, dp.value])
        }
      })
      const udGraph = this.graphRemoveDoubles(graph)
      this.sortGraph(udGraph)
      const newGraph = this.graphMedianBin(udGraph, 0.1)
      this.sortGraph(newGraph)
      return newGraph
    },
    graphRemoveDoubles: function (graph) {
      // FIXME: Performance!
      const newGraph = []
      graph.forEach(point => {
        let found = false
        newGraph.forEach(p => {
          if ((point[0].toString() === p[0].toString()) && (point[1].toString() === p[1].toString())) {
            found = true
          }
        })
        if (!found) {
          newGraph.push([point[0], point[1]])
        }
      })
      return newGraph
    },
    graphMedianBin: function (graph, binSize) {
      // FIXME: Performance!
      const valueCollections = {}
      graph.forEach(point => {
        const xval = point[0]
        const bin = (binSize / 2.0) + binSize * parseFloat((xval / binSize).toFixed(0))
        if (!(bin in valueCollections)) {
          valueCollections[bin] = []
        }
        valueCollections[bin].push(point[1])
      })
      const newGraph = []
      for (const [key, values] of Object.entries(valueCollections)) {
        this.sortValues(values)
        const median = values[Math.trunc(values.length / 2)]
        newGraph.push([parseFloat(key), median])
      }
      return newGraph
    },
    getValuePerTimestamp: function (ts) {
      // TODO Adapt to also interpolate if necessary?
      let res = null
      this.depthData.forEach(dataPoint => {
        if (dataPoint.ts.toString() === ts.toString()) {
          res = dataPoint.value
          return res
        }
      })
      return res
    },
    updateYAxisData: function (axisId, data, parameter, name = null) {
      name = name || parameter.parameter_name
      // Set yaxis data if not exists
      if (!(axisId in this.castChartOptions)) {
        this.castChartOptions.yAxis[axisId] = {
          visible: true,
          title: {
            text: name + ' [' + parameter.unit.symbol + ']', // TODO Parse if symbol already in name
            style: {
              color: this.castChartOptions.colors[axisId],
              fontWeight: 'bold'
            }
          },
          labels: {
            style: {
              color: this.castChartOptions.colors[axisId]
            }
          },
          margin: 5
        }
      }

      // Set series data
      this.castChartOptions.series[axisId] = {
        yAxis: axisId,
        data: data,
        animation: false,
        title: {
          text: name
        },
        name: name,
        visible: true,
        tooltip: {
          valueSuffix: ' ' + parameter.unit.symbol,
          valueDecimals: 3
        }
      }
    },
    clearChart: function () {
      // Remove data
      this.castChartOptions.series = []

      // Reset parameters
      this.datetime = 'Unknown'
      this.location = 'Unknown'
      this.device = 'Unknown'
      this.vessel = 'n/a'
    },
    sortGraph: function (graph) {
      graph.sort(function (a, b) {
        return a[0] === b[0] ? 0 : a[0] < b[0] ? -1 : 1
      })
    },
    sortValues: function (values) {
      values.sort(function (a, b) {
        return a === b ? 0 : a < b ? -1 : 1
      })
    }
  }
}
</script>

<style scoped>

#castChart{
  width: 100%;
}

.parameter-box {
  margin: 5px 0 0 5px;
}

.parameter-box {
  margin-bottom: 2px;
}

.parameter-name {
  font-size: 11px;
  color: #6c757d;
}

</style>
